import Phaser from "phaser";

export interface FloatingReward {
  icon: string;
  iconTexture?: string;
  label: string;
  color?: string;
  tileX?: number;
  tileY?: number;
}

interface ActiveFloater {
  container: Phaser.GameObjects.Container;
}

const SLOT_HEIGHT = 30;
const FADE_IN_MS = 220;
const HOLD_MS = 1400;
const FADE_OUT_MS = 800;
const DRIFT_PX = 80;
const OFFSET_X = 56;
const OFFSET_Y = -34;

export class FloatingRewardManager {
  private active: ActiveFloater[] = [];

  constructor(
    private scene: Phaser.Scene,
    private parent: Phaser.GameObjects.Container,
    private getAnchor: () => { x: number; y: number },
  ) {}

  spawn(reward: FloatingReward, position?: { x: number; y: number }): void {
    const anchor = position ?? this.getAnchor();
    const slotIdx = position ? 0 : this.active.length;
    const startX = anchor.x + OFFSET_X;
    const startY = anchor.y + OFFSET_Y + slotIdx * SLOT_HEIGHT;
    const endY = startY - DRIFT_PX;

    const container = this.scene.add.container(startX, startY);

    let iconNode: Phaser.GameObjects.GameObject & { x: number; y: number };
    if (reward.iconTexture && this.scene.textures.exists(reward.iconTexture)) {
      const img = this.scene.add.image(0, 0, reward.iconTexture).setOrigin(0, 0.5).setDisplaySize(26, 26);
      iconNode = img;
    } else {
      const iconText = this.scene.add.text(0, 0, reward.icon, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "22px",
      }).setOrigin(0, 0.5);
      iconText.setShadow(0, 1, "#000", 3, false, true);
      iconNode = iconText;
    }

    const labelText = this.scene.add.text(32, 0, reward.label, {
      fontFamily: "Cinzel, serif",
      fontSize: "20px",
      color: reward.color ?? "#fbbf24",
      fontStyle: "bold",
      stroke: "#000",
      strokeThickness: 4,
    }).setOrigin(0, 0.5);
    labelText.setShadow(0, 2, "#000", 4, false, true);

    container.add([iconNode, labelText]);
    container.setAlpha(0).setScale(0.7);
    container.setDepth(100);
    this.parent.add(container);

    const entry: ActiveFloater = { container };
    this.active.push(entry);

    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      scale: 1,
      duration: FADE_IN_MS,
      ease: "Back.easeOut",
    });

    this.scene.tweens.add({
      targets: container,
      y: endY,
      duration: HOLD_MS + FADE_OUT_MS,
      ease: "Sine.easeOut",
    });

    this.scene.tweens.add({
      targets: container,
      alpha: 0,
      duration: FADE_OUT_MS,
      delay: FADE_IN_MS + HOLD_MS,
      ease: "Sine.easeIn",
      onComplete: () => {
        container.destroy();
        this.active = this.active.filter((e) => e !== entry);
      },
    });
  }

  destroyAll(): void {
    for (const entry of this.active) entry.container.destroy();
    this.active = [];
  }
}
